import { 
  Mail, MessageSquare, AlertTriangle, Clock, ThumbsUp, ThumbsDown, 
  Briefcase, Calendar, Zap, ChevronUp, ChevronDown, ShieldAlert, 
  FileText, Lock, XCircle, CheckCircle, Globe
} from 'lucide-react';

import { cultureVibeBank } from '../../../data/CultureVibeBank';
import { visualRulesBank } from '../../../data/VisualRulesBank';
import { expenseRouletteBank } from '../../../data/ExpenseRouletteBank';
import { officeEtiquetteBank } from '../../../data/OfficeEtiquetteBank'; // --- NEW IMPORT ---

export const SWIPE_GAMES = {
  // --- MODULE 1: Channel Surfer ---
  'channel-surfer': {
    id: 'game_channel_surfer',
    preGameBriefing: {
        title: "The Comm Center",
        script: "Communication channels are not created equal. Urgent fire? Call. Paper trail needed? Email. Quick question? Slack. Choosing the wrong channel creates noise. Prove you know where to post.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },
    config: {
      mode: 'horizontal',
      instruction: "Slack (Left) or Email (Right)?",
      labels: { 
        A: { text: "Slack/Teams", icon: MessageSquare, color: "bg-purple-100 text-purple-600" }, 
        B: { text: "Email", icon: Mail, color: "bg-blue-100 text-blue-600" } 
      }
    },
    deck: [
      { id: 1, text: "Contract change (Need legal proof).", correctId: "B", feedback: "Paper trail secured. 📄" },
      { id: 2, text: "Hey, tacos for lunch?", correctId: "A", feedback: "Keep the inbox clean! 🌮" },
      { id: 3, text: "Detailed Q3 Project Update.", correctId: "B", feedback: "Good. Too long for chat." },
      { id: 4, text: "Quick yes/no question.", correctId: "A", feedback: "Speed is key. ⚡" },
      { id: 5, text: "You made a massive mistake that affects a client.", correctId: "B", feedback: "Email (or Zoom). Never bury bad news in Slack. Own it. 📧" },
      { id: 6, text: "Sending your boss a quick article on industry trends.", correctId: "A", feedback: "Slack. Keep it casual. 💬" },
      { id: 7, text: "Resigning from your position.", correctId: "B", feedback: "Email/Formal Letter. Legal paper trail required. 📧" },
      { id: 8, text: "Asking a coworker for the hex code of the brand blue.", correctId: "A", feedback: "Slack. Quick utility question. ⚡" },
      { id: 9, text: "HR wants a summary of a dispute you had with a vendor.", correctId: "B", feedback: "Email. This is a legal/HR document now. 📝" },
      { id: 10, text: "Can you jump on a quick 5-min huddle to share your screen?", correctId: "A", feedback: "Slack. Immediate action required. 💻" }
    ]
  },

  // --- MODULE 9: The Security Swipe ---
  'tech-security': {
    id: 'game_tech_security',
    preGameBriefing: {
        title: "The Weakest Link",
        script: "You are the new hire. That makes you the biggest security risk in the building. Hackers target you because you are eager to please. The Mission: You have incoming files. One is a phishing attack. If you click that link, you ransom the whole company.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },
    failureLogic: {
        'phishing_fail': {
            title: "Ransomware Installed",
            script: "Game Over. You just fell for 'False Urgency.' The email said 'DO THIS NOW' so you panicked and clicked. Always check the sender domain. You just cost the company millions.",
            videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        }
    },
    config: {
      mode: 'horizontal',
      instruction: "Delete (Left) or Open (Right)?",
      labels: { 
        A: { text: "Delete", icon: XCircle, color: "bg-red-100 text-red-600" }, 
        B: { text: "Open", icon: CheckCircle, color: "bg-green-100 text-green-600" } 
      }
    },
    deck: [
      { id: 1, text: "IT Support: Password Expiry Warning (sender: admin@company-support.net)", correctId: "A", interventionId: "phishing_fail", feedback: "Nice catch. 'company-support.net' is a fake domain." },
      { id: 2, text: "CEO: 'Are you at your desk?' (SMS)", correctId: "A", feedback: "Smishing attempt. The CEO doesn't text interns." },
      { id: 3, text: "Q3_Report_Final.pdf (from Manager)", correctId: "B", feedback: "Safe. Known sender, expected file." },
      { id: 4, text: "HR: Open Enrollment (sender: hr@company.com)", correctId: "B", feedback: "Safe. Internal domain." },
      { id: 5, text: "Your phone buzzes with an MFA (Multi-Factor) approval request, but you aren't logging in anywhere.", correctId: "A", interventionId: "phishing_fail", feedback: "MFA Fatigue Attack. Hackers spam you hoping you hit 'Approve' to make it stop. Report it." },
      { id: 6, text: "A flash drive sitting on the floor in the company parking lot.", correctId: "A", feedback: "Baiting Attack. Do not plug random USBs into a corporate laptop." },
      { id: 7, text: "CEO emails: 'I am in a meeting. Go buy $500 in Apple Gift Cards for a client NOW.'", correctId: "A", feedback: "Spear-Phishing. The CEO will never ask an entry-level employee to buy gift cards." },
      { id: 8, text: "Your laptop prompts a mandatory OS Security Patch via the official IT management software.", correctId: "B", feedback: "Safe. Keep your system updated." }
    ]
  },

  // --- MODULE 5: Culture Vibe ---
  'culture-vibe': {
    id: 'game_culture_vibe',
    title: "Decoding Culture",
    preGameBriefing: {
        title: "The Unwritten Rules",
        script: "Every office has a code, but it’s never written down. Dress too formal? You look stiff. Too casual? You look lazy. The Mission: Decide if these scenarios are 'Red Flags' (Run) or 'Green Flags' (Stay).",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },
    config: {
      mode: 'horizontal',
      instruction: "Red Flag (Left) or Green Flag (Right)?",
      labels: { 
        A: { text: "Toxic", icon: ThumbsDown, color: "bg-red-100 text-red-600" }, 
        B: { text: "Healthy", icon: ThumbsUp, color: "bg-green-100 text-green-600" } 
      }
    },
    deck: cultureVibeBank
  },

  // --- MODULE 5 (Part 2): Dress Code ---
  'dress-code': {
    id: 'game_dress_code',
    title: "The Visual Rules",
    preGameBriefing: {
        title: "Operation: Fit Check",
        script: "You wore a suit to a tech startup? You look like a narc. You wore a hoodie to a law firm? You look disrespectful. Visual hygiene extends beyond clothes to camera framing, background noise, and digital presence. Read the room.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },
    failureLogic: {
        'fashion_fail': {
            title: "Observe Then Calibrate",
            script: "You guessed wrong. The Fix: Week 1, dress boring (Business Casual). Watch your boss. If they wear jeans, you wear jeans. Never dress lower than the boss, never dress higher than the client.",
            videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        }
    },
    config: {
      mode: 'horizontal',
      instruction: "Fail (Left) or Fit (Right)?",
      labels: { 
        A: { text: "Fail", icon: AlertTriangle, color: "bg-red-100 text-red-600" }, 
        B: { text: "Fit", icon: Briefcase, color: "bg-blue-100 text-blue-600" } 
      }
    },
    deck: visualRulesBank
  },

  // --- MODULE 12: Time Management ---
  'gatekeeper': {
    id: 'game_gatekeeper',
    preGameBriefing: {
        title: "The Urgency Trap",
        script: "Your to-do list is a lie. It treats 'Check Email' the same as 'Finish Major Project.' The Mission: Sort the noise from the signal. Don't let the 'Urgent' kill the 'Important'.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },
    failureLogic: {
        'priority_fail': {
            title: "High-Friction Prioritization",
            script: "You prioritized a low-impact task over a critical bottleneck. Identify the most complex task and execute it first. Do not check asynchronous channels until the primary blocker is cleared.",
            videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        }
    },
    config: {
      mode: 'vertical',
      instruction: "Do Now (Up) or Schedule (Down)?",
      upLabel: { text: "Do It Now", icon: Zap, color: "bg-red-100 text-red-600" },
      downLabel: { text: "Schedule It", icon: Calendar, color: "bg-blue-100 text-blue-600" }
    },
    deck: [
      { id: 1, text: "Server is DOWN.", correctDirection: "up", feedback: "Emergency! Fix it." },
      { id: 2, text: "Brainstorming for next quarter.", correctDirection: "down", feedback: "Deep work. Schedule it." },
      { id: 3, text: "Boss texts: 'Call me ASAP.'", correctDirection: "up", feedback: "Don't ignore the boss." },
      { id: 4, text: "Coworker: 'Have a sec to chat?'", correctDirection: "down", interventionId: "priority_fail", feedback: "Protect your flow state." }
    ]
  },

  // --- MODULE 2 (AI Literacy PART 3): Shadow AI Defender ---
  'shadow-ai-defender': {
    id: 'game_shadow_ai_defender',
    preGameBriefing: {
        title: "Shadow AI Protocol",
        script: "Data defense is an enterprise priority. Mass usage of unsanctioned public AI models causes critical corporate information leaks daily. Your mission: Audit workspace assets. Swipe LEFT for 'Enterprise Only' (Proprietary source files or PII) and RIGHT for 'Public AI Safe' (non-sensitive/utility items). WARNING: Leaking customer PII or internal financials to a public LLM results in an immediate compliance failure.",
        videoUrl: null
    },
    failureLogic: {
        'shadow_ai_breach': {
            title: "CRITICAL DATA BREACH",
            script: "Security Failure: You copy-pasted sensitive enterprise parameters into a public AI interface. Public models store inputs for algorithmic refinement training, permanently leaking proprietary assets out of corporate bounds. In production, this causes client lawsuits and immediate contract termination.",
            videoUrl: null
        }
    },
    config: {
      mode: 'horizontal',
      instruction: "Enterprise Gated (Left) or Public AI Safe (Right)?",
      labels: { 
        A: { text: "Enterprise Only", icon: Lock, color: "bg-red-100 text-red-600" }, 
        B: { text: "Public AI Safe", icon: Globe, color: "bg-green-100 text-green-600" } 
      }
    },
    deck: [
      {
        "id": 1,
        "text": "HR: Uploading a spreadsheet of Q3 employee performance scores and salary brackets to generate a 'compensation equity report'.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Employee salaries and performance scores are highly confidential PII. Public AI models use inputs for training, causing a massive internal privacy violation."
      },
      {
        "id": 2,
        "text": "HR: Asking the AI to write a generic, legally compliant job description for a mid-level 'Project Manager' role.",
        "correctId": "B",
        "feedback": "Safe. Generating standard, non-proprietary job descriptions contains no internal company data and is perfectly fine."
      },
      {
        "id": 3,
        "text": "HR: Pasting a top candidate's resume, including their home address, email, and phone number, to summarize their experience.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. You just leaked a candidate's Personally Identifiable Information (PII) to a public database, violating data privacy laws."
      },
      {
        "id": 4,
        "text": "HR: Asking the AI to suggest 5 fun, virtual icebreaker questions for the monthly team all-hands meeting.",
        "correctId": "B",
        "feedback": "Safe. Generic team-building ideas rely on public domain knowledge and expose zero corporate data."
      },
      {
        "id": 5,
        "text": "HR: Uploading an employee's detailed medical leave request and doctor's note to ask the AI to summarize the restrictions.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Exposing HIPAA-protected medical data to a public AI violates severe federal compliance laws and creates massive corporate liability."
      },
      {
        "id": 6,
        "text": "Sales: Uploading the raw CRM export of your top 100 enterprise clients, including their annual spend, to find cross-sell opportunities.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Customer lists and revenue data are the lifeblood of the company. Leaking this provides training data to your competitors."
      },
      {
        "id": 7,
        "text": "Sales: Asking the AI to draft a generic 'Cold Outreach' email template targeting purchasing managers in the logistics industry.",
        "correctId": "B",
        "feedback": "Safe. Generic sales templates targeting an industry contain no proprietary client data or internal negotiation strategy."
      },
      {
        "id": 8,
        "text": "Sales: Pasting a draft contract containing specific discount margins offered exclusively to a Fortune 500 client.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. You just leaked your enterprise negotiation tactics and private client pricing to a public AI model."
      },
      {
        "id": 9,
        "text": "Sales: Asking the AI to summarize the general benefits of cloud computing so you can add it to a sales slide deck.",
        "correctId": "B",
        "feedback": "Safe. Asking for publicly known industry definitions relies on public data and poses zero risk."
      },
      {
        "id": 10,
        "text": "Sales: Pasting a generic sales pitch and asking the AI to rewrite it to sound more enthusiastic and persuasive.",
        "correctId": "B",
        "feedback": "Safe. As long as there are no specific prospect names or internal pricing mentioned, rewriting generic marketing copy is safe."
      },
      {
        "id": 11,
        "text": "Finance: Uploading the unreleased Q3 financial earnings report to generate an executive summary before the public board meeting.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Leaking unreleased financial data violates strict SEC regulations and invites catastrophic insider trading risks."
      },
      {
        "id": 12,
        "text": "Finance: Asking the AI to generate a generic Excel formula to calculate a compound annual growth rate (CAGR).",
        "correctId": "B",
        "feedback": "Safe. Standard mathematical formulas and spreadsheet syntax are universal and contain no sensitive corporate data."
      },
      {
        "id": 13,
        "text": "Finance: Pasting a massive block of unredacted corporate credit card transactions to ask the AI to categorize the spending.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Financial ledgers and vendor transaction histories require secure local sandboxes. Never upload raw financial data to public models."
      },
      {
        "id": 14,
        "text": "Finance: Uploading a spreadsheet of corporate credit card numbers and employee names to generate an automated 'past due' reminder email.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. You just leaked live corporate credit card numbers and employee identities to a public AI model, risking massive financial fraud."
      },
      {
        "id": 15,
        "text": "Finance: Asking the AI to explain the standard differences between GAAP and IFRS accounting standards for a training presentation.",
        "correctId": "B",
        "feedback": "Safe. Standard accounting principles are universal, public knowledge and contain no sensitive corporate data."
      },
      {
        "id": 16,
        "text": "Legal/Compliance: Uploading a confidential settlement agreement draft with a former employee to check for grammatical errors.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Settlement drafts contain highly sensitive legal strategy and PII. Uploading this breaks attorney-client privilege and confidentiality clauses."
      },
      {
        "id": 17,
        "text": "Legal/Compliance: Asking the AI to draft a generic, boiler-plate Non-Disclosure Agreement (NDA) template without inputting any specific company or client names.",
        "correctId": "B",
        "feedback": "Safe. Requesting a standard legal template with zero proprietary inputs exposes no corporate data."
      },
      {
        "id": 18,
        "text": "Legal/Compliance: Pasting an email from an internal whistleblower reporting executive fraud to summarize the allegations for the board.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Exposing internal investigations and whistleblower identities to a public model is a massive compliance and ethical violation."
      },
      {
        "id": 19,
        "text": "Legal/Compliance: Asking the AI to summarize the general requirements of the GDPR (General Data Protection Regulation) for a high-level slide deck.",
        "correctId": "B",
        "feedback": "Safe. Public regulatory frameworks and laws are already public data. Asking for a summary poses no security risk."
      },
      {
        "id": 20,
        "text": "Legal/Compliance: Asking the AI to provide professional synonyms for the word 'indemnification' to make a generic presentation easier to read.",
        "correctId": "B",
        "feedback": "Safe. Vocabulary and grammar checks on single, non-sensitive words are completely safe and expose no context."
      },
      {
        "id": 21,
        "text": "General Operations: Uploading the transcript of a closed-door executive meeting regarding a potential competitor acquisition to get a quick summary.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Mergers and acquisitions strategy is highly confidential material. Leaking this to a public model risks massive market manipulation and regulatory fines."
      },
      {
        "id": 22,
        "text": "General Operations: Asking the AI to write a polite, generic reminder email to the office about cleaning out the communal refrigerator on Fridays.",
        "correctId": "B",
        "feedback": "Safe. Routine office logistics with no proprietary data or strategic value are completely fine to process through public AI."
      },
      {
        "id": 23,
        "text": "General Operations: Pasting a vendor's detailed cybersecurity vulnerability report of your internal network to ask the AI for a remediation plan.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Leaking network vulnerabilities gives public AI models (and anyone who scrapes their data) the exact blueprint to hack your company."
      },
      {
        "id": 24,
        "text": "General Operations: Asking the AI to outline a standard 5-step agenda for running an effective daily project stand-up meeting.",
        "correctId": "B",
        "feedback": "Safe. General meeting management frameworks and productivity methodologies are public knowledge."
      },
      {
        "id": 25,
        "text": "General Operations: Uploading the proprietary, unreleased product roadmap for the next two years to generate a bulleted presentation outline.",
        "correctId": "A",
        "interventionId": "shadow_ai_breach",
        "feedback": "CRITICAL BREACH. Future product roadmaps are heavily guarded trade secrets. Exposing them ruins your competitive advantage."
      }
    ]
  },

  // --- ETHICS: EXPENSE ROULETTE ---
  'expense-roulette': {
    id: 'game_expense_roulette',
    preGameBriefing: {
        title: "The Corporate Card",
        script: "You just got your first corporate credit card. It feels like free money. It's not. Finance audits everything. The Mission: Swipe Left for 'Fraud' (You pay for it / Get fired) and Right for 'Expensable' (The company pays).",
        videoUrl: null
    },
    failureLogic: {
        'expense_fail': {
            title: "Audit Failed",
            script: "You just violated the Travel & Expense (T&E) policy. Finance rejected the claim, and that massive charge is coming out of your paycheck.",
            videoUrl: null
        }
    },
    config: {
      mode: 'horizontal',
      instruction: "Fraud (Left) or Expensable (Right)?",
      labels: { 
        A: { text: "Fraud", icon: AlertTriangle, color: "bg-red-100 text-red-600" }, 
        B: { text: "Expensable", icon: CheckCircle, color: "bg-green-100 text-green-600" } 
      }
    },
    deck: expenseRouletteBank
  },
  
  // --- ETHICS: OFFICE ETIQUETTE (UPDATED) ---
  'office-etiquette': {
    id: 'game_office_etiquette',
    preGameBriefing: {
        title: "Unwritten Rules",
        script: "The office has unwritten rules. Break them, and you become the office pariah. Swipe LEFT for 'Career Limiting Move' (CLM). Swipe RIGHT for 'Pro Move'.",
        videoUrl: null
    },
    config: {
      mode: 'horizontal',
      instruction: "CLM (Left) or Pro Move (Right)?",
      labels: { 
        A: { text: "CLM", icon: XCircle, color: "bg-red-100 text-red-600" }, 
        B: { text: "Pro Move", icon: CheckCircle, color: "bg-blue-100 text-blue-600" } 
      }
    },
    deck: officeEtiquetteBank // <-- REPLACES HARDCODED ARRAY
  }
};